import './VPAIDHandler.css';
import Handler from './Handler';
import VPAIDHTML5Client from 'vpaid-html5-client';
import { update } from 'Emitter';
import { ExpiringPromise } from 'Services/Misc/timing';
import { getInstance } from 'StateManager';
import { MEDIA, UI } from 'StateEvents';
import * as is from 'Misc/is';
import * as Formats from '../MediaFormats';
import * as VPAIDEvents from 'Enum/VPAIDEvents';
import VPAIDMediaMeasurer from 'Measurement/VPAIDMediaMeasurer';

export default class VPAIDHandler extends Handler {
  constructor(options, adParameters = '') {
    super(options);
    this.timeoutDuration = 8000;
    this.startPending = false;
    this.adParameters = adParameters;
    getInstance().registerListener(new VPAIDMediaMeasurer());
  }

  async configure(videoEl, container, hostSettings = {}) {
    container = this.createContainer(container);
    const window = container.ownerDocument.defaultView;
    this.client = new VPAIDHTML5Client(container, videoEl, { extraOptions: { window } });
    this.outerHost = hostSettings;
    return await this.loadAdUnit();
  }

  loadAdUnit() {
    return new ExpiringPromise((resolve, reject) => {
      this.client.loadAdUnit(this.src, (err, adUnit) => {
        try {
          if(err) {
            reject(err);
            return;
          }
          this.initialize(adUnit).then(resolve);
        } catch(e) {
          reject(e);
        }
      });
    }, this.timeoutDuration, 'loadAdUnit');
  }

  async initialize(adUnit) {
    this.adUnit = adUnit;

    Object
      .values(VPAIDEvents)
      .forEach( eventName => adUnit.subscribe(eventName, this.onVPAIDEvent.bind(this, eventName)));

    this.version = await this.getVPAIDVersion(adUnit);
    this.ready   = await this.initializeAdUnit(adUnit);
    this.startPolling();
  }

  getVPAIDVersion(adUnit) {
    return new ExpiringPromise((resolve, reject) => {
      adUnit.handshakeVersion('2.0', (err, version) => {
        version = Number(version);

        if(isNaN(version) || version < 2) {
          reject('VPAID version too low');
        } else {
          resolve(version);
        }
      });
    }, this.timeoutDuration, 'getVPAIDVersion');
  }

  initializeAdUnit(adUnit) {
    return new ExpiringPromise((resolve, reject) => {
      adUnit.subscribe(VPAIDEvents.AD_LOADED, resolve.bind(this, true));
      const host = this.outerHost;
      adUnit.initAd(host.width, host.height, host.viewMode, host.bitrate, { AdParameters: this.adParameters }, {});
    }, this.timeoutDuration, 'initAd');
  }


  onVPAIDEvent(eventName, ...args) {
    update(MEDIA.MESSAGE_RECEIVED, 'onVPAIDEvent', [eventName, ...args]);
    switch(eventName) {
      case VPAIDEvents.AD_CLICK_THRU:
        if(args && args.length && args[0].playerHandles && args[0].url) {
          if(args[0].url) {
            window.open(args[0].url, '_blank');
          } else if(this.clickthru) {
            window.open(this.clickthru, '_blank');
          }
        }
        update(UI.CLICKED, 'onVPAIDEvent');
        break;
      case VPAIDEvents.AD_DURATION_CHANGE:
      case VPAIDEvents.AD_REMAINING_TIME_CHANGE:
        if(!this.duration || this.duration === -2) {
          this.adUnit.getAdDuration(this.handleResult(this.onDurationChange, duration => this.duration = duration));
          this.adUnit.getAdRemainingTime(this.handleResult(this.onDurationChange, remaining => this.duration = remaining));
        }
        break;
      case VPAIDEvents.AD_STOPPED:
        this.onEnded();
        break;
    }
  }

  get clickthru() {
    return getInstance().state && getInstance().state.configuration.UI.click;
  }

  onDurationChange() {}
  onTimeUpdate(event) {}
  onStarted(event) {}
  onEnded(event) {
    this.stopPolling();
  }

  start() {
    if(!this.startPending) {
      this.adUnit.subscribe(VPAIDEvents.AD_STARTED, (err, result) => {
        update(MEDIA.STARTED, 'start');
        this.startPending = false;
        this.started = true;
      });
      this.play();
    }
  }

  async play() {
    if(this.started) {
      return await this.callAdUnit('resumeAd');
    } else if(!this.startPending) {
      this.startPending = true;
      return await this.callAdUnit('startAd');
    }
  }

  async pause() {
    return await this.callAdUnit('pauseAd');
  }

  async setVolume(volume) {
    return await this.callAdUnit('setVolume', volume);
  }

  async resize(width, height, viewMode) {
    return await this.callAdUnit('resizeAd', width, height, viewMode);
  }

  callAdUnit(method, ...args) {
    if(this.adUnit && is.fun(this.adUnit[method])) {
      return new ExpiringPromise((resolve, reject) => {
        this.adUnit[method](...args, (err, result) => {
          if(err) {
            reject(err);
          } else {
            resolve(result);
          }
        });
      }, this.timeoutDuration, method);
    }
  }

  startPolling() {
    this.pollID = setInterval(() => {
      this.adUnit.getAdRemainingTime(this.handleResult(this.onTimeUpdate, remaining => {
        this.currentTime = this.duration - remaining;
      }));
    }, 250);
  }

  stopPolling() {
    clearInterval(this.pollID);
  }

  createContainer(rootContainer) {
    const vpaidContainer = document.createElement('div');
    vpaidContainer.className = 'aaVPAIDContainer';
    rootContainer.appendChild(vpaidContainer);
    return vpaidContainer;
  }

  handleResult(fn, propHandler) {
    return (err, result) => {
      propHandler(result);
      fn({ target: this });
    }
  }
}


// WEBPACK FOOTER //
// ./src/Media/Handlers/VPAIDHandler.js